import React, { useCallback, useEffect, useState } from "react";
import { FileText } from "lucide-react";
import { api, downloadFile } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { Button, Card } from "@/components/app/Primitives";
import { StackPage } from "@/components/app/Layout";
import { CenterSpinner } from "@/components/app/Spinner";

export default function Reports() {
  const { activeWorkspace } = useAuth();
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!activeWorkspace) return;
    setLoading(true);
    try { setRows((await api("/reports/productivity", { query: { workspace_id: activeWorkspace.id } })).rows || []); }
    catch (e) { toast.show(e instanceof Error ? e.message : "Failed to load", "error"); }
    finally { setLoading(false); }
  }, [activeWorkspace, toast]);
  useEffect(() => { load(); }, [load]);

  const exportExcel = async () => {
    if (!activeWorkspace) return;
    setExporting(true);
    try {
      await downloadFile(`/reports/tasks.xlsx?workspace_id=${activeWorkspace.id}`, `assignoo-tasks-${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.show("Excel downloaded", "success");
    } catch (e) { toast.show(e instanceof Error ? e.message : "Export failed", "error"); } finally { setExporting(false); }
  };
  const maxProd = Math.max(...rows.map((r) => r.productivity), 100);

  return (
    <StackPage title="Reports" testID="reports-page" contentClassName="p-4 space-y-3">
      <div className="mb-1">
        <p className="text-xl font-extrabold text-a-text">Team productivity</p>
        <p className="mt-1 text-[13px] text-a-muted">Ranking by completion rate of assigned tasks in this workspace.</p>
      </div>
      <Button testID="reports-export" label={exporting ? "Preparing…" : "Export tasks to Excel (.xlsx)"} loading={exporting} onPress={exportExcel} icon={<FileText size={16} />} />
      {loading ? <CenterSpinner /> : rows.length === 0 ? (
        <Card><p className="text-a-muted">No data yet. Create tasks and assign to members to see productivity.</p></Card>
      ) : rows.map((r, i) => (
        <Card key={r.user_id} testID={`report-row-${r.user_id}`}>
          <div className="space-y-2.5">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-a-dim text-sm font-extrabold text-a-primary">#{i + 1}</span>
              <div className="min-w-0 flex-1"><p className="truncate font-bold text-a-text">{r.full_name}</p><p className="text-xs text-a-muted">{r.role} · {r.completed}/{r.total} tasks</p></div>
              <p className="text-xl font-extrabold text-a-text">{r.productivity}%</p>
            </div>
            <div className="h-2 overflow-hidden rounded-lg bg-a-border"><div className="h-2 rounded-lg bg-a-primary transition-[width] duration-700" style={{ width: `${(r.productivity / maxProd) * 100}%` }} /></div>
          </div>
        </Card>
      ))}
    </StackPage>
  );
}
